// Shared, interactive temperature-versus-depth chart used by the map and
// location conditions pages. This module deliberately stays independent of
// the map controller so content pages do not download the map bundle.
import { html, joinHtml, setHtml } from "./html.js";
import { convertUnitValue, formatUnitValue, unitPreference, unitSymbol } from "./units.js";

const DEPTH_UNITS_PER_METER = { ft: 3.28084, fm: 1 / 1.8288, m: 1 };
const ZOOM_PRESETS = { ft: [50, 100, 200], fm: [10, 25, 50, 100, 200], m: [15, 30, 60] };
const UNIFORM_SPAN = { F: 0.5, C: 0.5 / 1.8 };
const mountedProfiles = new Set();
let unitChangeListenerInstalled = false;

const depthUnit = () => unitPreference("depth") || "m";
const temperatureUnit = () => unitPreference("waterTemperature") === "F" ? "F" : "C";
const depthUnitsPerMeter = (unit = depthUnit()) => DEPTH_UNITS_PER_METER[unit] || 1;
const toDisplayDepth = (meters) => meters * depthUnitsPerMeter();
const toDisplayTemperature = (celsius) => Math.round(convertUnitValue(celsius, "C", temperatureUnit()) * 10) / 10;
const depthLabel = (meters) => !Number.isFinite(Number(meters)) || Number(meters) <= 0.25
  ? "Surface" : formatUnitValue(meters, "depth", "m", { decimals: 0 });
const temperatureLabel = (celsius) => formatUnitValue(celsius, "waterTemperature", "C", { decimals: 1 });

export function niceStep(range, count = 5) {
  const raw = Math.max(range, 1e-9) / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].find((factor) => factor * magnitude >= raw) * magnitude;
  return step;
}

export function niceTicks(minimum, maximum, count = 5) {
  const step = niceStep(maximum - minimum, count);
  const ticks = [];
  for (let value = Math.ceil(minimum / step - 1e-9) * step; value <= maximum + 1e-9; value += step) ticks.push(Number(value.toFixed(6)));
  return ticks;
}

export function temperatureAtDepth(values, depthMeters) {
  if (!values.length) return null;
  if (depthMeters <= values[0].depthMeters) return values[0].temperatureC;
  for (let index = 1; index < values.length; index += 1) {
    const shallow = values[index - 1], deep = values[index];
    if (depthMeters <= deep.depthMeters) {
      const fraction = (depthMeters - shallow.depthMeters) / Math.max(deep.depthMeters - shallow.depthMeters, 1e-9);
      return shallow.temperatureC + fraction * (deep.temperatureC - shallow.temperatureC);
    }
  }
  return values[values.length - 1].temperatureC;
}

export function zoomOptions(maxDepthMeters, unit = depthUnit()) {
  const maxDisplay = maxDepthMeters * depthUnitsPerMeter(unit);
  return (ZOOM_PRESETS[unit] || ZOOM_PRESETS.m).filter((preset) => preset < maxDisplay * 0.8);
}

export function thermoclineBand(thermocline) {
  if (!thermocline) return null;
  const top = Number(thermocline.topDepthMeters ?? thermocline.depthMeters);
  const bottom = Math.max(top, Number(thermocline.bottomDepthMeters ?? top));
  return Number.isFinite(top) ? { top, bottom, thickness: bottom - top } : null;
}

export function depthRangeLabel(band) {
  if (!band) return "";
  const top = depthLabel(band.top), bottom = depthLabel(band.bottom);
  return top === bottom ? top : `${top.replace(/\s*(fm|ft|m)$/, "")}–${bottom}`;
}

export function noThermoclineLabel(profile) {
  if (["mixed", "gradual"].includes(profile?.noThermocline)) return "";
  return "Thermocline estimate unavailable";
}

export function thicknessLabel(band) {
  return band && toDisplayDepth(band.thickness) >= 1
    ? `${formatUnitValue(band.thickness, "depth", "m", { decimals: 0 })} thick` : "a thin layer";
}

export function automaticZoom(profile, unit = depthUnit()) {
  const values = profile.values || [];
  const maxDepth = Math.max(...values.map((item) => item.depthMeters), 1);
  const band = thermoclineBand(profile.thermocline);
  if (!band) return 0;
  const wanted = depthUnitsPerMeter(unit) * Math.max(band.top * 1.8, band.bottom * 1.25);
  return zoomOptions(maxDepth, unit).find((preset) => preset >= wanted) || 0;
}

function sortedValues(profile) {
  return [...(profile.values || [])]
    .filter((item) => Number.isFinite(item.depthMeters) && Number.isFinite(item.temperatureC))
    .sort((a, b) => a.depthMeters - b.depthMeters);
}

function statsHtml(profile) {
  const band = thermoclineBand(profile.thermocline);
  const classified = ["mixed", "gradual"].includes(profile.noThermocline);
  const detail = noThermoclineLabel(profile);
  const thermoclineCard = band
    ? html`<div class="wc-stat"><span>Thermocline</span><strong>${depthLabel(band.top)}</strong><small>To ${depthLabel(band.bottom)} · ${thicknessLabel(band)}</small></div>`
    : html`<div class="wc-stat"><span>Thermocline</span><strong>${classified ? "No clear thermocline" : "Estimate unavailable"}</strong>${detail ? html`<small>${detail}</small>` : ""}</div>`;
  return html`<div class="wc-stats">${thermoclineCard}</div>`;
}

function zoomHtml(values, zoom) {
  const maxDepth = values[values.length - 1].depthMeters;
  const unit = depthUnit();
  const presets = zoomOptions(maxDepth, unit);
  if (!presets.length) return "";
  const button = (value, label) => html`<button type="button" data-wc-zoom="${value}" aria-pressed="${zoom === value}">${label}</button>`;
  return html`<div class="wc-zoom" role="group" aria-label="Depth shown">${button(0, "Whole column")}${joinHtml([...presets].reverse().map((preset) => button(preset, `Top ${preset} ${unit}`)), "")}</div>`;
}

export function waterColumnProfileHtml(profile, zoom) {
  const values = sortedValues(profile);
  if (!values.length) return html`<p class="wc-note">No valid temperature levels are available for this profile.</p>`;
  return html`${statsHtml(profile)}${zoomHtml(values, zoom)}
    <div class="wc-chart" data-wc-chart tabindex="0" role="img" aria-label="Water temperature by depth. Use the up and down arrow keys to read each model level."></div>
    <p class="wc-hint">Hover, tap or focus the profile to inspect a temperature by depth.</p>`;
}

export function waterColumnChartSvg(profile, zoom, width, height) {
  const values = sortedValues(profile);
  if (!values.length) return "";
  const unit = depthUnit(), tempUnit = temperatureUnit();
  const fullDepthDisplay = toDisplayDepth(values[values.length - 1].depthMeters);
  const maxDisplayDepth = zoom > 0 ? Math.min(zoom, fullDepthDisplay) : fullDepthDisplay;
  const maxDepthMeters = maxDisplayDepth / depthUnitsPerMeter(unit);
  const shown = values.filter((item) => item.depthMeters <= maxDepthMeters + 1e-6);
  const edge = temperatureAtDepth(values, maxDepthMeters);
  if (shown.length && shown[shown.length - 1].depthMeters < maxDepthMeters - 1e-6 && edge !== null) shown.push({ depthMeters: maxDepthMeters, temperatureC: edge, interpolated: true });

  const exact = shown.map((item) => convertUnitValue(item.temperatureC, "C", tempUnit));
  const uniform = Math.max(...exact) - Math.min(...exact) < UNIFORM_SPAN[tempUnit];
  const uniformTemp = Math.round(exact.reduce((sum, value) => sum + value, 0) / exact.length * 10) / 10;
  const plotTemp = (celsius) => uniform ? uniformTemp : toDisplayTemperature(celsius);
  const temperatures = shown.map((item) => plotTemp(item.temperatureC));
  let minTemp = Math.min(...temperatures), maxTemp = Math.max(...temperatures);
  const minimumSpan = tempUnit === "F" ? 4 : 2;
  if (maxTemp - minTemp < minimumSpan) {
    const middle = (maxTemp + minTemp) / 2;
    minTemp = middle - minimumSpan / 2;
    maxTemp = middle + minimumSpan / 2;
  }
  const tempTicks = niceTicks(minTemp, maxTemp, width < 420 ? 3 : 5);
  minTemp = Math.min(minTemp, tempTicks[0]);
  maxTemp = Math.max(maxTemp, tempTicks[tempTicks.length - 1]);

  const plot = { left: 58, right: width - 14, top: 46, bottom: height - 14 };
  const x = (temperature) => plot.left + (temperature - minTemp) / Math.max(maxTemp - minTemp, 1e-9) * (plot.right - plot.left);
  const y = (depthMeters) => plot.top + toDisplayDepth(depthMeters) / Math.max(maxDisplayDepth, 1e-9) * (plot.bottom - plot.top);
  const depthTicks = niceTicks(0, maxDisplayDepth, Math.max(4, Math.round((plot.bottom - plot.top) / 70)));
  const depthTick = (display) => display === 0 ? "Surface" : `${Number(display.toFixed(1))} ${unit}`;
  const gridY = joinHtml(depthTicks.map((display) => {
    const meters = display / depthUnitsPerMeter(unit);
    return html`<line class="wc-grid" x1="${plot.left}" x2="${plot.right}" y1="${y(meters)}" y2="${y(meters)}"/><text class="wc-axis" x="${plot.left - 8}" y="${y(meters) + 4}" text-anchor="end">${depthTick(display)}</text>`;
  }), "");
  const gridX = joinHtml(tempTicks.map((temperature) => html`<line class="wc-grid" x1="${x(temperature)}" x2="${x(temperature)}" y1="${plot.top}" y2="${plot.bottom}"/><text class="wc-axis" x="${x(temperature)}" y="${plot.top - 10}" text-anchor="middle">${Number(temperature.toFixed(1))}°</text>`), "");

  const thermocline = profile.thermocline;
  const note = noThermoclineLabel(profile);
  const mixedLabel = thermocline || !note ? "" : (() => {
    const onLeft = x(plotTemp(values[0].temperatureC)) > (plot.left + plot.right) / 2;
    return html`<text class="wc-chart-note" x="${onLeft ? plot.left + 8 : plot.right - 8}" y="${plot.top + 16}" text-anchor="${onLeft ? "start" : "end"}">${note}</text>`;
  })();
  const linePoints = shown.map((item) => `${x(plotTemp(item.temperatureC)).toFixed(1)},${y(item.depthMeters).toFixed(1)}`);
  const band = thermoclineBand(thermocline);
  const bandBottom = band ? Math.min(band.bottom, maxDepthMeters) : 0;
  const tagText = band ? `Thermocline ${depthRangeLabel(band)}` : "";
  const tagWidth = Math.round(tagText.length * 6.4 + 18);
  const thermoclineBandShade = band && band.top <= maxDepthMeters && bandBottom > band.top
    ? html`<rect class="wc-thermocline-band" x="${plot.left}" width="${plot.right - plot.left}" y="${y(band.top)}" height="${y(bandBottom) - y(band.top)}"/>` : "";
  const thermoclineMarker = band && band.top <= maxDepthMeters
    ? html`<line class="wc-thermocline-line" x1="${plot.left}" x2="${plot.right}" y1="${y(band.top)}" y2="${y(band.top)}"/>${band.bottom <= maxDepthMeters && band.thickness > 0 ? html`<line class="wc-thermocline-line is-bottom" x1="${plot.left}" x2="${plot.right}" y1="${y(band.bottom)}" y2="${y(band.bottom)}"/>` : ""}<g class="wc-thermocline-tag" transform="translate(${plot.left + 6} ${y(band.top) - 11})"><rect width="${tagWidth}" height="20" rx="10"/><text x="${tagWidth / 2}" y="14" text-anchor="middle">${tagText}</text></g>` : "";
  const dots = joinHtml(shown.filter((item) => !item.interpolated).map((item) => html`<circle class="wc-dot" cx="${x(plotTemp(item.temperatureC))}" cy="${y(item.depthMeters)}" r="3.5"/>`), "");

  return html`<svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true" data-plot-left="${plot.left}" data-plot-right="${plot.right}" data-plot-top="${plot.top}" data-plot-bottom="${plot.bottom}" data-max-depth="${maxDepthMeters}" data-min-temp="${minTemp}" data-max-temp="${maxTemp}" data-uniform-temp="${uniform ? uniformTemp : ""}">
    ${thermoclineBandShade}${gridY}${gridX}${mixedLabel}
    <text class="wc-axis wc-axis-title" x="${plot.left}" y="13">Temperature (${unitSymbol("waterTemperature")})</text>
    <polyline class="wc-line" points="${linePoints.join(" ")}"/>
    ${dots}${thermoclineMarker}
    <g class="wc-cursor" visibility="hidden"><line class="wc-cursor-line" x1="${plot.left}" x2="${plot.right}" y1="0" y2="0"/><circle class="wc-cursor-dot" r="6"/></g>
  </svg><div class="wc-readout" hidden aria-live="polite"></div>`;
}

function draw(state) {
  const chart = state.container.querySelector("[data-wc-chart]");
  if (!chart) return;
  const width = Math.max(280, Math.round(chart.clientWidth || state.container.clientWidth || 600));
  const viewportHeight = globalThis.window?.innerHeight || 700;
  const height = Math.round(Math.max(300, Math.min(520, width * 0.78, viewportHeight * 0.55)));
  setHtml(chart, waterColumnChartSvg(state.profile, state.zoom, width, height));
}

function showReadout(state, depthMeters) {
  const container = state.container;
  const svg = container.querySelector("[data-wc-chart] svg");
  const readout = container.querySelector(".wc-readout");
  if (!svg || !readout) return;
  const values = sortedValues(state.profile);
  const plot = { left: Number(svg.dataset.plotLeft), right: Number(svg.dataset.plotRight), top: Number(svg.dataset.plotTop), bottom: Number(svg.dataset.plotBottom) };
  const maxDepth = Number(svg.dataset.maxDepth), minTemp = Number(svg.dataset.minTemp), maxTemp = Number(svg.dataset.maxTemp);
  const depth = Math.max(0, Math.min(maxDepth, depthMeters));
  const temperature = temperatureAtDepth(values, depth);
  if (temperature === null) return;
  const cursorY = plot.top + depth / Math.max(maxDepth, 1e-9) * (plot.bottom - plot.top);
  const plotted = svg.dataset.uniformTemp ? Number(svg.dataset.uniformTemp) : toDisplayTemperature(temperature);
  const cursorX = plot.left + (plotted - minTemp) / Math.max(maxTemp - minTemp, 1e-9) * (plot.right - plot.left);
  const cursor = svg.querySelector(".wc-cursor");
  cursor.setAttribute("visibility", "visible");
  cursor.querySelector("line").setAttribute("y1", cursorY);
  cursor.querySelector("line").setAttribute("y2", cursorY);
  cursor.querySelector("circle").setAttribute("cx", cursorX);
  cursor.querySelector("circle").setAttribute("cy", cursorY);
  readout.hidden = false;
  readout.textContent = `${temperatureLabel(temperature)} at ${depthLabel(depth)}`;
  const rect = svg.getBoundingClientRect();
  const scale = rect.width / Number(svg.viewBox.baseVal.width || rect.width || 1);
  const chart = container.querySelector("[data-wc-chart]");
  readout.style.top = `${Math.max(0, cursorY * scale - 34)}px`;
  readout.style.left = `${Math.min(Math.max(cursorX * scale, 90), Math.max(90, chart.clientWidth - 90))}px`;
  state.depth = depth;
}

function hideReadout(container) {
  container.querySelector(".wc-cursor")?.setAttribute("visibility", "hidden");
  const readout = container.querySelector(".wc-readout");
  if (readout) readout.hidden = true;
}

function depthFromPointer(svg, event) {
  const rect = svg.getBoundingClientRect();
  const scale = Number(svg.viewBox.baseVal.height || rect.height) / rect.height;
  const top = Number(svg.dataset.plotTop), bottom = Number(svg.dataset.plotBottom);
  const yInSvg = (event.clientY - rect.top) * scale;
  return (yInSvg - top) / Math.max(bottom - top, 1e-9) * Number(svg.dataset.maxDepth);
}

function render(state) {
  setHtml(state.container, waterColumnProfileHtml(state.profile, state.zoom));
  draw(state);
}

function onUnitChange() {
  for (const state of mountedProfiles) {
    if (!state.container.isConnected) {
      state.observer?.disconnect();
      mountedProfiles.delete(state);
      continue;
    }
    state.zoom = automaticZoom(state.profile);
    state.depth = null;
    render(state);
  }
}

export function renderWaterColumnProfile(container, profile) {
  if (!container || !profile?.values?.length) {
    if (container) setHtml(container, html`<p class="wc-note">A water-column temperature profile is unavailable.</p>`);
    return;
  }
  const state = { container, profile, zoom: automaticZoom(profile), depth: null, observer: null };
  mountedProfiles.add(state);
  if (!unitChangeListenerInstalled) {
    document.addEventListener("glc-units-changed", onUnitChange);
    unitChangeListenerInstalled = true;
  }
  container.addEventListener("pointermove", (event) => {
    if (!event.target.closest?.("[data-wc-chart]")) return;
    const svg = container.querySelector("[data-wc-chart] svg");
    if (svg) showReadout(state, depthFromPointer(svg, event));
  });
  container.addEventListener("pointerdown", (event) => {
    const chart = event.target.closest?.("[data-wc-chart]");
    if (!chart) return;
    chart.setPointerCapture?.(event.pointerId);
    const svg = chart.querySelector("svg");
    if (svg) showReadout(state, depthFromPointer(svg, event));
  });
  container.addEventListener("pointerleave", (event) => {
    if (event.pointerType === "mouse") hideReadout(container);
  });
  container.addEventListener("keydown", (event) => {
    if (!event.target.closest?.("[data-wc-chart]") || !["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const svg = container.querySelector("[data-wc-chart] svg");
    const levels = sortedValues(state.profile).map((item) => item.depthMeters).filter((depth) => depth <= Number(svg?.dataset.maxDepth ?? Infinity) + 1e-6);
    const current = Number.isFinite(state.depth) ? state.depth : -1;
    const next = event.key === "Home" ? levels[0]
      : event.key === "End" ? levels[levels.length - 1]
        : event.key === "ArrowDown" ? levels.find((depth) => depth > current + 1e-6) ?? levels[levels.length - 1]
          : [...levels].reverse().find((depth) => depth < current - 1e-6) ?? levels[0];
    if (Number.isFinite(next)) showReadout(state, next);
  });
  container.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-wc-zoom]");
    if (!button) return;
    state.zoom = Number(button.dataset.wcZoom);
    render(state);
  });
  if (typeof ResizeObserver === "function") {
    let lastWidth = 0;
    state.observer = new ResizeObserver(() => {
      const width = Math.round(container.clientWidth);
      if (width && width !== lastWidth) { lastWidth = width; draw(state); }
    });
    state.observer.observe(container);
  }
  render(state);
}
